import { Tooltip, message } from "antd";
import { apiHost } from "consts";
import bem from "easy-bem";
import CaseShell from "features/cases/blocks/CaseShell";
import { usePublish } from "features/cases/hooks/usePublish";
import { arePrimaryAttrsFilled } from "features/cases/utils";
import { useMarkOnboardingSeen, useOnboarding } from "features/onboarding";
import { useDataQuery } from "hooks/useData";
import { useUser } from "hooks/useUser";
import React, { useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router";

import "./Solution.css";

const cn = bem("solution");

const collectivePersonId = 2;

interface OwnProps {
  problemId?: string;
  solutionId?: string;
  solsExpanded?: boolean;
  setSolsExpanded?: any;
  setProbExpanded?: (expanded: boolean) => void;
  probExpanded?: boolean;
  setCaseExpanded?: (expanded: boolean) => void;
  caseExpanded?: boolean;
  isNewSolutionPage?: boolean;
}

const Solution: React.FC<OwnProps> = ({
  problemId,
  solutionId,
  setSolsExpanded,
  setProbExpanded,
  probExpanded,
  setCaseExpanded,
  caseExpanded = false,
  isNewSolutionPage = false,
}) => {
  const {
    isEditor,
    isAdmin,
    isContributor,
    userId,
    isBannedFromPublishing,
    canPublishAsCollective,
  } = useUser();

  const attrs: any[] = useDataQuery("attrs/?attributeOf=solution");

  const attrCards = useDataQuery(
    `problems/${problemId}/solutions/${solutionId}/cards/`,
    isNewSolutionPage,
    [],
  );

  const problem = useDataQuery(`problems/${problemId}`, !problemId, null);

  const isProblemPublished = problem?.meta?.isDraft === false;

  const primaryAttrsFilled = arePrimaryAttrsFilled(attrs, attrCards);

  const disabled = !primaryAttrsFilled || !isProblemPublished;

  const disabledHint = !primaryAttrsFilled
    ? "Нельзя опубликовать материалы по проблеме пока не заполнены главные атрибуты"
    : "Нельзя опубликовать решение если проблема не опубликована";

  const solutionList = useDataQuery(`problems/${problemId}/solutions/`);

  const solution = useMemo(() => {
    if (!solutionList) return null;

    return solutionList.find((sol: any) => sol.id == solutionId);
  }, [solutionList, solutionId]);

  const isDraft = solution?.meta?.isDraft;

  const contentMeta = solution?.meta || {};
  const isMyStuff = userId && contentMeta.user === userId;
  const isCollectiveContent = contentMeta.author === collectivePersonId;
  // Коллективным контентом можно управлять только при действующем праве
  // публиковать от имени коллективного разума; одного авторства недостаточно.
  const canActOnOwnContent =
    isMyStuff && (!isCollectiveContent || canPublishAsCollective);
  const canPublish =
    isAdmin || (isContributor && canActOnOwnContent && !isBannedFromPublishing);

  const navigate = useNavigate();

  // После публикации: сообщение + переход на страницу проблемы с подсветкой
  // и прокруткой к только что опубликованной карточке решения в блоке решений.
  const onPublishSuccess = useCallback(
    (val: number) => {
      if (val !== 1) return;

      message.success("Решение опубликовано");
      navigate({
        pathname: `/problems/${problemId}`,
        hash: `#solution-${solutionId}`,
      });
    },
    [problemId, solutionId, navigate],
  );

  const onPublishClick = usePublish(
    (val) => `${apiHost}/solutions/api/solutions/${solutionId}/publish/${val}`,
    `problems/${problemId}/solutions/`,
    onPublishSuccess,
  );

  const markOnboardingSeen = useMarkOnboardingSeen();
  const refSaveButton = useRef(null);
  const refSphereInput = useRef(null);

  const { tourNode: tourNewSolution } = useOnboarding({
    topic: "solution-create",
    refs: [undefined, refSphereInput, refSaveButton],
    condition: attrCards?.length === 0 && !solution?.title,
    onOpen: () => markOnboardingSeen("reading"),
  });

  const { tourNode: tourSolutionCards } = useOnboarding({
    topic: "solution-cards",
    refs: [undefined, refSphereInput, refSaveButton],
    condition: attrCards?.length === 0 && !!solution?.title,
    onOpen: () => markOnboardingSeen("reading"),
  });

  if (!solution && !isNewSolutionPage) {
    return <div>Solution not found.</div>;
  }

  if (
    !isNewSolutionPage &&
    (solution.meta.isDeleted || solution.meta.isDraft)
  ) {
    if (!isMyStuff && !isEditor && !isAdmin) {
      return <div>Solution not found or unavailable.</div>;
    }
  }

  const footer = (
    <>
      {canPublish && (
        <span className={cn("publish-status")}>
          {isDraft && (
            <Tooltip
              placement="bottomRight"
              title={disabled ? disabledHint : ""}
            >
              <button
                className={cn("publish-button")}
                disabled={disabled}
                onClick={() => onPublishClick(1)}
              >
                Опубликовать решение
              </button>
            </Tooltip>
          )}
        </span>
      )}
    </>
  );

  return (
    <CaseShell
      kind="solution"
      problemId={problemId}
      solutionId={solutionId}
      isAdding={isNewSolutionPage}
      initialScroll="second"
      setSolsExpanded={setSolsExpanded}
      probExpanded={probExpanded}
      setProbExpanded={setProbExpanded}
      setCaseExpanded={setCaseExpanded}
      caseExpanded={caseExpanded}
      onboardingNodes={
        <>
          {isContributor && tourNewSolution}
          {isContributor && tourSolutionCards}
        </>
      }
      footer={footer}
    />
  );
};

export default Solution;
